import * as React from 'react';
import { toast } from 'react-toastify';
import { Action, Dispatch } from 'redux';

import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { OptionsViewModel } from '@models/common.models';
import { AutoAuthoViewModel } from '@services/AutoAuthoService';

import { AppState } from '..';

import { AutoAuthoPage, AutoAuthoResult } from './_components/auto-autho.component';

import { autoAuthoService, ConfirmationViewModel, ConfirmationRequestViewModel } from '@services/AutoAuthoService';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum AutoAuthoActionType {
  AuthorizeFailure = 'AuthorizeFailure',
  AuthorizeStart = 'AuthorizeStart',
  AuthorizeSuccess = 'AuthorizeSuccess',
  GetSwoTripStatusesFailure = 'GetSwoTripStatusesFailure',
  GetSwoTripStatusesStart = 'GetSwoTripStatusesStart',
  GetSwoTripStatusesSuccess = 'GetSwoTripStatusesSuccess',
  GetViewModelFailure = 'GetViewModelFailure',
  GetViewModelStart = 'GetViewModelStart',
  GetViewModelSuccess = 'GetViewModelSuccess',
  GotoPage = 'GotoPage',
  UpdateViewModel = 'UpdateViewModel'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.
interface AutoAuthoAction extends Action {
  readonly type: AutoAuthoActionType;
}

export interface AuthorizeFailureAction extends AutoAuthoAction {}
export interface AuthorizeStartAction extends AutoAuthoAction {}
export interface AuthorizeSuccessAction extends AutoAuthoAction {
  readonly result: AutoAuthoResult;
}

export interface GetSwoTripStatusesFailureAction extends AutoAuthoAction {}
export interface GetSwoTripStatusesStartAction extends AutoAuthoAction {}
export interface GetSwoTripStatusesSuccessAction extends AutoAuthoAction {
  readonly statuses: OptionsViewModel[];
}

export interface GetViewModelFailureAction extends AutoAuthoAction {
  readonly message?: string;
}
export interface GetViewModelStartAction extends AutoAuthoAction {}
export interface GetViewModelSuccessAction extends AutoAuthoAction {
  readonly rememberPhoneNumber: boolean;
  readonly phoneNumber: string;
  readonly swoId: number;
  readonly viewModel: AutoAuthoViewModel;
}

export interface GotoPageAction extends AutoAuthoAction {
  readonly page: AutoAuthoPage;
}

export interface UpdateViewModelAction extends AutoAuthoAction {
  readonly viewModel: AutoAuthoViewModel;
}

// Actions for each action type declared here.
// They should all return an action object.
export const authorize = (phoneNumber: string, swoId: number, request: ConfirmationRequestViewModel) => {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    try {
      dispatch(authorizeStart());
      const confirmationResult: ConfirmationViewModel = await autoAuthoService.confirmAutoAutho(
        phoneNumber,
        swoId,
        request
      );
      dispatch(
        authorizeSuccess({
          ...confirmationResult,
          authorizationAmount: request.authorizationAmount!
        })
      );
    } catch (e) {
      dispatch(authorizeFailure());
    }
  };
};

export function authorizeFailure(): AuthorizeFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Error retrieving data from network. Please try again later.'} />,
    ToastOptions.Error
  );
  return {
    type: AutoAuthoActionType.AuthorizeFailure
  };
}

export function authorizeStart(): AuthorizeStartAction {
  return {
    type: AutoAuthoActionType.AuthorizeStart
  };
}

export function authorizeSuccess(result: AutoAuthoResult): AuthorizeSuccessAction {
  toast.success(<ToastComponent type={ToastType.Success} message={`Authorized Successfully`} />, ToastOptions.Success);
  return {
    type: AutoAuthoActionType.AuthorizeSuccess,
    result: result
  };
}

export const getSwoTripStatuses = (phoneNumber: string, swoId: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getSwoTripStatusesStart());
    try {
      const results = await autoAuthoService.getSwoPortalStatuses(phoneNumber, swoId);
      dispatch(getSwoTripStatusesSuccess(results.results));
    } catch (e) {
      dispatch(getSwoTripStatusesFailure());
    }
  };
};

export function getSwoTripStatusesFailure(): GetSwoTripStatusesFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Error retrieving data from network. Please try again later.'} />,
    ToastOptions.Error
  );
  return {
    type: AutoAuthoActionType.GetSwoTripStatusesFailure
  };
}

export function getSwoTripStatusesSuccess(statuses: OptionsViewModel[]): GetSwoTripStatusesSuccessAction {
  return {
    type: AutoAuthoActionType.GetSwoTripStatusesSuccess,
    statuses: statuses
  };
}

export function getSwoTripStatusesStart(): GetSwoTripStatusesStartAction {
  return {
    type: AutoAuthoActionType.GetSwoTripStatusesStart
  };
}

export const getViewModel = (phoneNumber: string, swoId: number, rememberPhoneNumber: boolean) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getViewModelStart());
    try {
      const viewModel: AutoAuthoViewModel | null = await autoAuthoService.getAutoAuthoViewModel(phoneNumber, swoId);
      if (viewModel) {
        dispatch(getViewModelSuccess(viewModel, phoneNumber, rememberPhoneNumber, swoId));
      } else {
        dispatch(getViewModelFailure('Invalid phone number or SWO ID. Please try again.'));
      }
    } catch (e) {
      dispatch(getViewModelFailure('Error retrieving data from network. Please try again later.'));
    }
  };
};

export function getViewModelFailure(message?: string): GetViewModelFailureAction {
  if (message) {
    toast.error(<ToastComponent type={ToastType.Error} message={message} />, ToastOptions.Error);
  }
  return {
    type: AutoAuthoActionType.GetViewModelFailure,
    message: message
  };
}

export function getViewModelStart(): GetViewModelStartAction {
  return {
    type: AutoAuthoActionType.GetViewModelStart
  };
}

export function getViewModelSuccess(
  viewModel: AutoAuthoViewModel,
  phoneNumber: string,
  rememberPhoneNumber: boolean,
  swoId: number
): GetViewModelSuccessAction {
  return {
    type: AutoAuthoActionType.GetViewModelSuccess,
    phoneNumber: phoneNumber,
    rememberPhoneNumber: rememberPhoneNumber,
    swoId: swoId,
    viewModel: viewModel
  };
}

export function gotoPage(page: AutoAuthoPage): GotoPageAction {
  return {
    type: AutoAuthoActionType.GotoPage,
    page: page
  };
}

export function updateViewModel(viewModel: AutoAuthoViewModel): UpdateViewModelAction {
  return {
    type: AutoAuthoActionType.UpdateViewModel,
    viewModel: viewModel
  };
}



// WEBPACK FOOTER //
// ./src/auto-autho/auto-autho.actions.tsx